/** One applied CRDT event, matching the engine's AppliedOp CDC (JSON fields decoded at the boundary). */
export type AppliedOp = {
  readonly kind: string
  readonly node: string
  readonly entity: string | null
  readonly key: string | null
  readonly value: string | null
  readonly oldValue: string | null
  readonly fields: Readonly<Record<string, unknown>>
  readonly content: string | null
  readonly qid: string | null
}

export type BlockMutationCallback<Write, Context> = (events: readonly AppliedOp[], context: Context) => readonly Write[]

/** Route one applied mutation CDC to registered product projections before their transaction commits. */
export function createMutationFanout<Write, Context>(callbacks: Readonly<Record<string, BlockMutationCallback<Write, Context>>>) {
  return (events: readonly AppliedOp[], context: Context): Write[] => {
    const groups = new Map<BlockMutationCallback<Write, Context>, AppliedOp[]>()
    for (const event of events) {
      // The engine's type replacement CDC uses NUL-prefixed type and omits the old type.
      // Notify registered callbacks so they can remove writes belonging to the previous type.
      const types = event.kind === 'set' && event.key === '\u0000type' ? Object.keys(callbacks) : [event.fields.type]
      if (event.kind === 'set' && event.key === 'type' && event.oldValue !== null) types.push(JSON.parse(event.oldValue))
      const notified = new Set<BlockMutationCallback<Write, Context>>()
      for (const type of types) {
        const callback = typeof type === 'string' ? callbacks[type] : undefined
        if (!callback || notified.has(callback)) continue
        notified.add(callback)
        const group = groups.get(callback)
        if (group) group.push(event)
        else groups.set(callback, [event])
      }
    }
    return [...groups].flatMap(([callback, group]) => callback(group, context))
  }
}
